import {
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
} from 'react';
import { useTranslation } from 'react-i18next';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import SearchIcon from '@mui/icons-material/Search';
import { cn } from '@/utils/cn';
import type { SelectOption } from '@/components/ui/Select';
import { FieldLabel, parseFieldLabel } from '@/components/ui/FieldLabel';
import { useDebounce } from '@/hooks/useDebounce';
import { FloatingListbox } from '@/components/ui/FloatingListbox';
import { normalizeSelectOptions } from '@/components/ui/selectOptionUtils';

export interface SearchableMultiSelectProps {
  label?: string;
  ariaLabel?: string;
  error?: string;
  hint?: string;
  options: SelectOption[];
  values?: string[];
  onChange?: (values: string[]) => void;
  disabled?: boolean;
  loading?: boolean;
  searching?: boolean;
  className?: string;
  id?: string;
  name?: string;
  required?: boolean;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  filterLocally?: boolean;
  onSearchQuery?: (query: string) => void;
  searchDebounceMs?: number;
  /** Resolve chip label when option is not in the current list */
  getOptionLabel?: (value: string) => string | undefined;
  /** Allow clearing all selections with an inline X on the trigger (default: not required). */
  allowClear?: boolean;
  /**
   * Content fixed beside the input (e.g. action button). Stays aligned with the
   * control when selection chips grow below.
   */
  trailing?: ReactNode;
}

export function SearchableMultiSelect({
  label,
  ariaLabel,
  error,
  hint: _hint,
  options,
  values = [],
  onChange,
  disabled,
  loading,
  searching = false,
  className,
  id,
  name,
  required,
  placeholder: placeholderProp,
  searchPlaceholder: searchPlaceholderProp,
  emptyMessage: emptyMessageProp,
  filterLocally = true,
  onSearchQuery,
  searchDebounceMs = 300,
  getOptionLabel,
  allowClear,
  trailing,
}: SearchableMultiSelectProps) {
  const { t } = useTranslation('common');
  const placeholder = placeholderProp ?? t('actions.select');
  const searchPlaceholder = searchPlaceholderProp ?? t('picker.search');
  const emptyMessage = emptyMessageProp ?? t('picker.noMatches');
  const autoId = useId();
  const fieldId = id ?? name ?? autoId;
  const { required: isRequired } = parseFieldLabel(label, required);
  const anchorRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const onSearchQueryRef = useRef(onSearchQuery);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(-1);
  const debouncedQuery = useDebounce(query, searchDebounceMs);
  const serverSearch = Boolean(onSearchQuery);
  const safeOptions = useMemo(() => normalizeSelectOptions(options), [options]);
  const canClear = allowClear ?? !isRequired;
  const showClearButton = canClear && values.length > 0 && !disabled && !loading;

  const labelByValue = useMemo(() => {
    const map = new Map<string, string>();
    for (const opt of safeOptions) map.set(opt.value, opt.label);
    return map;
  }, [safeOptions]);

  useEffect(() => {
    onSearchQueryRef.current = onSearchQuery;
  }, [onSearchQuery]);

  useEffect(() => {
    if (!serverSearch) return;
    onSearchQueryRef.current?.(debouncedQuery);
  }, [debouncedQuery, serverSearch]);

  const filtered = useMemo(() => {
    if (!filterLocally || serverSearch) return safeOptions;
    const q = query.trim().toLowerCase();
    if (!q) return safeOptions;
    return safeOptions.filter(
      (opt) => opt.label.toLowerCase().includes(q) || opt.value.toLowerCase().includes(q),
    );
  }, [safeOptions, query, filterLocally, serverSearch]);

  const selectableFiltered = useMemo(
    () => filtered.filter((opt) => !opt.disabled),
    [filtered],
  );
  const activeOption = activeIndex >= 0 ? selectableFiltered[activeIndex] : undefined;

  const allFilteredSelected =
    selectableFiltered.length > 0 &&
    selectableFiltered.every((opt) => values.includes(opt.value));

  const anyFilteredSelected = selectableFiltered.some((opt) => values.includes(opt.value));
  const selectedValuesKey = values.join('\u0000');
  const listboxId = `${fieldId}-listbox`;
  const errorId = `${fieldId}-error`;
  const optionId = (optionValue: string) =>
    `${listboxId}-option-${encodeURIComponent(optionValue).replaceAll('%', '-')}`;

  useEffect(() => {
    if (!open) {
      setQuery('');
      setActiveIndex(-1);
      return;
    }
    const selectedValueSet = new Set(selectedValuesKey ? selectedValuesKey.split('\u0000') : []);
    const selectedIndex = selectableFiltered.findIndex((option) => selectedValueSet.has(option.value));
    setActiveIndex(selectedIndex >= 0 ? selectedIndex : selectableFiltered.length > 0 ? 0 : -1);
    window.setTimeout(() => searchRef.current?.focus(), 0);
  }, [open, selectableFiltered, selectedValuesKey]);

  useEffect(() => {
    if (!open || activeIndex < 0) return;
    const activeValue = selectableFiltered[activeIndex]?.value ?? '';
    document
      .getElementById(
        `${listboxId}-option-${encodeURIComponent(activeValue).replaceAll('%', '-')}`,
      )
      ?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex, listboxId, open, selectableFiltered]);

  const toggle = (next: string) => {
    if (values.includes(next)) {
      onChange?.(values.filter((v) => v !== next));
    } else {
      onChange?.([...values, next]);
    }
  };

  const remove = (next: string) => {
    onChange?.(values.filter((v) => v !== next));
  };

  const handleClearAll = (e: ReactMouseEvent) => {
    e.stopPropagation();
    onChange?.([]);
    setOpen(false);
  };

  const handleSelectAll = (e: ReactMouseEvent) => {
    e.stopPropagation();
    const toAdd = selectableFiltered.map((opt) => opt.value);
    onChange?.([...new Set([...values, ...toAdd])]);
  };

  const handleUnselectAll = (e: ReactMouseEvent) => {
    e.stopPropagation();
    const filteredValues = new Set(selectableFiltered.map((opt) => opt.value));
    onChange?.(values.filter((v) => !filteredValues.has(v)));
  };

  const toggleOpen = () => {
    if (!disabled && !loading) setOpen((o) => !o);
  };

  const handleKeyDown = (event: ReactKeyboardEvent) => {
    if (event.key === 'Escape' && open) {
      event.preventDefault();
      event.stopPropagation();
      setOpen(false);
      triggerRef.current?.focus();
      return;
    }
    if (event.key === 'Tab') {
      setOpen(false);
      return;
    }
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      if (!open) {
        setOpen(true);
        return;
      }
      if (selectableFiltered.length === 0) return;
      setActiveIndex((current) => {
        const start = current < 0 ? (event.key === 'ArrowDown' ? -1 : 0) : current;
        return event.key === 'ArrowDown'
          ? (start + 1) % selectableFiltered.length
          : (start - 1 + selectableFiltered.length) % selectableFiltered.length;
      });
      return;
    }
    if (event.key === 'Home' && open) {
      event.preventDefault();
      setActiveIndex(selectableFiltered.length > 0 ? 0 : -1);
      return;
    }
    if (event.key === 'End' && open) {
      event.preventDefault();
      setActiveIndex(selectableFiltered.length - 1);
      return;
    }
    if (event.key === 'Enter' && open && activeOption) {
      event.preventDefault();
      toggle(activeOption.value);
    }
  };

  const resolveLabel = (value: string) =>
    labelByValue.get(value) ?? getOptionLabel?.(value) ?? `#${value}`;

  const summary =
    values.length === 0
      ? placeholder
      : values.length === 1
        ? resolveLabel(values[0])
        : `${values.length} selected`;

  return (
    <div className={cn('min-w-0 w-full', className)}>
      <FieldLabel htmlFor={fieldId} label={label} required={required} />
      <div className="flex min-w-0 items-center gap-2">
          <div ref={anchorRef} className="min-w-0 flex-1">
            <div
              className={cn(
                'input flex min-w-0 w-full items-center gap-1 p-0',
                error && 'input-error',
                (disabled || loading) && 'cursor-not-allowed opacity-60',
              )}
            >
              <button
                ref={triggerRef}
                type="button"
                id={fieldId}
                disabled={disabled || loading}
                role="combobox"
                aria-haspopup="listbox"
                aria-label={ariaLabel}
                aria-expanded={open}
                aria-controls={open ? listboxId : undefined}
                aria-activedescendant={
                  open && activeOption ? optionId(activeOption.value) : undefined
                }
                aria-invalid={error ? true : undefined}
                aria-describedby={error ? errorId : undefined}
                onClick={toggleOpen}
                onKeyDown={handleKeyDown}
                className={cn(
                  'flex min-w-0 flex-1 items-center px-3 py-2 text-left',
                  values.length === 0 && 'text-slate-500 dark:text-slate-400',
                  values.length > 0 && 'text-slate-900 dark:text-white',
                  (disabled || loading) && 'cursor-not-allowed',
                )}
              >
                <span className="truncate">{loading ? 'Loading…' : summary}</span>
              </button>
              {showClearButton && (
                <button
                  type="button"
                  aria-label={t('labels.clearAllSelections')}
                  onClick={handleClearAll}
                  className="shrink-0 rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-700 dark:hover:text-slate-200"
                >
                  <CloseIcon sx={{ fontSize: 16 }} />
                </button>
              )}
              <button
                type="button"
                disabled={disabled || loading}
                aria-label={open ? 'Close options' : 'Open options'}
                onClick={toggleOpen}
                className={cn(
                  'shrink-0 px-2 py-2 text-slate-500 dark:text-slate-400',
                  (disabled || loading) && 'cursor-not-allowed',
                )}
              >
                <KeyboardArrowDownIcon
                  className={cn('transition-transform', open && 'rotate-180')}
                  fontSize="small"
                />
              </button>
            </div>
            <FloatingListbox
              open={open}
              onOpenChange={setOpen}
              anchorRef={anchorRef}
              id={`${listboxId}-popover`}
            >
                <div className="border-b border-slate-100 p-2 dark:border-slate-700">
                  <div className="relative">
                    <SearchIcon
                      className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400"
                      sx={{ fontSize: 18 }}
                    />
                    <input
                      ref={searchRef}
                      type="search"
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                      placeholder={searchPlaceholder}
                      className="input w-full py-1.5 pl-8 pr-2 text-sm"
                      aria-label={searchPlaceholder}
                      role="combobox"
                      aria-expanded={open}
                      aria-controls={listboxId}
                      aria-activedescendant={
                        activeOption ? optionId(activeOption.value) : undefined
                      }
                      onKeyDown={handleKeyDown}
                    />
                  </div>
                </div>
                {selectableFiltered.length > 0 && (
                  <div className="flex items-center gap-1.5 border-b border-slate-100 px-3 py-2 text-xs dark:border-slate-700">
                    <button
                      type="button"
                      disabled={allFilteredSelected}
                      onClick={handleSelectAll}
                      className="font-medium text-primary-600 hover:text-primary-700 hover:underline disabled:cursor-not-allowed disabled:text-slate-400 disabled:no-underline dark:text-primary-400 dark:hover:text-primary-300 dark:disabled:text-slate-500"
                    >
                      Select all
                    </button>
                    <span className="text-slate-300 dark:text-slate-600" aria-hidden>
                      |
                    </span>
                    <button
                      type="button"
                      disabled={!anyFilteredSelected}
                      onClick={handleUnselectAll}
                      className="font-medium text-primary-600 hover:text-primary-700 hover:underline disabled:cursor-not-allowed disabled:text-slate-400 disabled:no-underline dark:text-primary-400 dark:hover:text-primary-300 dark:disabled:text-slate-500"
                    >
                      Unselect all
                    </button>
                  </div>
                )}
                <ul
                  id={listboxId}
                  role="listbox"
                  aria-labelledby={fieldId}
                  aria-multiselectable="true"
                  className="min-h-0 flex-1 overflow-y-auto py-1"
                >
                  {filtered.length === 0 && (
                    <li
                      role="status"
                      aria-live="polite"
                      className="px-3 py-2 text-sm text-slate-500 dark:text-slate-400"
                    >
                      {searching ? 'Searching…' : emptyMessage}
                    </li>
                  )}
                  {filtered.map((opt) => {
                    const checked = values.includes(opt.value);
                    return (
                      <li
                        key={opt.value}
                        id={optionId(opt.value)}
                        role="option"
                        aria-selected={checked}
                        aria-disabled={opt.disabled || undefined}
                      >
                        <button
                          type="button"
                          disabled={opt.disabled}
                          className={cn(
                            'flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-slate-800 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50 dark:text-slate-100 dark:hover:bg-slate-700',
                            checked && 'bg-primary-50 font-medium text-primary-700 dark:bg-primary-950/50 dark:text-primary-300',
                            activeIndex >= 0 &&
                              selectableFiltered[activeIndex]?.value === opt.value &&
                              'bg-slate-100 dark:bg-slate-700',
                          )}
                          onClick={() => !opt.disabled && toggle(opt.value)}
                        >
                          <span
                            className={cn(
                              'flex h-4 w-4 shrink-0 items-center justify-center rounded border text-[10px]',
                              checked
                                ? 'border-primary-600 bg-primary-600 text-white'
                                : 'border-slate-300 dark:border-slate-600',
                            )}
                          >
                            {checked ? '✓' : ''}
                          </span>
                          <span className="truncate">{opt.label}</span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
            </FloatingListbox>
          </div>
          {trailing ? <div className="shrink-0">{trailing}</div> : null}
        </div>
      {name && values.map((v) => (
        <input key={v} type="hidden" name={name} value={v} readOnly />
      ))}
      {values.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {values.map((v) => (
            <span
              key={v}
              className="inline-flex max-w-full items-center gap-1 rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-700 dark:bg-slate-800 dark:text-slate-200"
            >
              <span className="truncate">{resolveLabel(v)}</span>
              {!disabled && (
                <button
                  type="button"
                  className="rounded-full p-0.5 hover:bg-slate-200 dark:hover:bg-slate-700"
                  aria-label={`Remove ${resolveLabel(v)}`}
                  onClick={() => remove(v)}
                >
                  <CloseIcon sx={{ fontSize: 14 }} />
                </button>
              )}
            </span>
          ))}
        </div>
      )}
      {error && <p id={errorId} className="mt-1 text-xs text-red-600" role="alert">{error}</p>}
    </div>
  );
}
